import { getSimpleForBuyRequest } from "@/js/helpers/buy-request"
import type { IRogue, BuyRequest } from "@/js/services/RogueTypes"
import type { MassStorageOptions } from "../../RackTypes"
import { extractNamedShelfSelections } from "../../utils/ms-utils/ms-shelf-utils"
import { findBundleProduct } from "../../utils/product-lookup"
import { getCornerSelection } from "../../utils/selection-utils"
import { MassStorageBuilder } from "./massStorageBuilder"

export function createMassStorage(rogue: IRogue, buyRequest: BuyRequest) {
  const rack = getSimpleForBuyRequest(buyRequest, rogue)

  const widthSelection  = rack.attributes.mass_width?.toString().replace('"', '') || '43'
  const heightSelection = rack.attributes.mass_height?.toString().replace('"', '') || '43'

  let footSelection = findBundleProduct(rogue, buyRequest, 'wheels', true)?.name.toLowerCase().includes('wheels') ? 'wheels' : 'feet'

  // Remove the base rack and all accessories - we only want addon units which will have a side defined
  const addonUnits            = buyRequest.children?.filter(child => child.metadata?.side)
  const addonUnitHeightIs100  = addonUnits?.some(unit => unit.metadata?.height === "100")

  // Force feet selection for 3 or more units or if any additional unit is 100"
  if (addonUnits && addonUnits.length >= 3 || addonUnitHeightIs100) {
    footSelection = 'feet'
  }

  const {
    shelfOneSelection, shelfTwoSelection, shelfThreeSelection, shelfFourSelection, shelfFiveSelection,
    shelfOneHangerQuantity, shelfTwoHangerQuantity, shelfThreeHangerQuantity, shelfFourHangerQuantity, shelfFiveHangerQuantity,
  } = extractNamedShelfSelections(rogue, buyRequest)

  const leftAddonChain: object[] = []
  const rightAddonChain: object[] = []

  if (addonUnits) {
    addonUnits.forEach((childBuyRequest, i) => {
      const addonShelf          = getSimpleForBuyRequest(childBuyRequest, rogue)
      const addonHeightSelection = addonShelf.attributes.mass_height?.toString().replace('"', '') || '43'
      const addonWidthSelection  = addonShelf.attributes.mass_width?.toString().replace('"', '') || '43'
      const side                = childBuyRequest.metadata?.side

      const shelfSelections = extractNamedShelfSelections(rogue, childBuyRequest)
      let cornerSelection   = findBundleProduct(rogue, childBuyRequest, 'corner', true)?.name
      let cornerQuantity    = getCornerSelection(cornerSelection)

      if (addonWidthSelection.toLocaleLowerCase() === 'corner') {
        cornerSelection = findBundleProduct(rogue, childBuyRequest, 'corner', true)?.name ? findBundleProduct(rogue, childBuyRequest, 'corner', true)?.name : 'Mass Storage Corner Shelf (2 Pack)'
        cornerQuantity = getCornerSelection(cornerSelection) ? cornerQuantity = getCornerSelection(cornerSelection) : cornerQuantity = getCornerSelection(cornerSelection) || '2'
      } else {
        cornerQuantity = ''
      }

      const addonUnit = {
        accordionIndex  : i + 1,
        heightSelection : addonHeightSelection,
        widthSelection  : addonWidthSelection,
        cornerQuantity,
        ...shelfSelections,
        footSelection,
      }

      if (side === 'left') {
        leftAddonChain.push(addonUnit)
      } else {
        rightAddonChain.push(addonUnit)
      }
    })
  }

  // Any unit addon will have the onVirtualMeshClick() on the BR meta
  const openAccordionByIndex = buyRequest.children
    ?.filter(child => typeof child.metadata?.onVirtualMeshClick === 'function')
    ?.at(0)?.metadata?.onVirtualMeshClick

  const config: MassStorageOptions = {
    baseAccordionIndex : 0,
    line               : 'RM',
    hardware           : 'isonite',
    height             : heightSelection,
    width              : widthSelection,
    footSelection,
    shelfOneSelection,
    shelfTwoSelection,
    shelfThreeSelection,
    shelfFourSelection,
    shelfFiveSelection,
    shelfOneHangerQuantity,
    shelfTwoHangerQuantity,
    shelfThreeHangerQuantity,
    shelfFourHangerQuantity,
    shelfFiveHangerQuantity,
    leftAddonChain,
    rightAddonChain,
    onVirtualMeshClick : openAccordionByIndex,
  }

  const part = new MassStorageBuilder(config, buyRequest).build()

  return { config, part }
}
